/**
 * Vuex module for voicemail messages stack
 */
import debug from 'debug'
import VMail from 'Store/class/vmail'
import events from 'Store/events'
import API from '@/api'
import Vue from 'vue'
import VoicemailLocalStorage from '@/api/voicemail'

const dbg = debug('store:action:vmail:')

const pbx2bool = val => {
  return /^yes$/i.test(val) || ~~val === 1
}

// API returns filtered messages and sometimes grep also some extra
// messages like those with caller id "Unvailable".
// This method fixes the problem. Also filters empty results
const greetMsg = (data) => {
  if (!data || !Array.isArray(data) || !data[0]) {
    return null
  }
  const folders = ['/greet', '/unavail', '/busy', '/temp']
  return data.find(el => folders.some(f => el.dir.endsWith(f)))
}

const actions = {
  init: async function ({ commit, dispatch, rootGetters, state }) {
    dbg('Init application: voicemail messages and settings')
    commit('SET_INITIAL_LOADING', true)
    return API.getVMBoxesToWatch().then(({ data }) => {
      if (!data || !Array.isArray(data)) {
        throw new Error('Failed to get voicemail boxes to watch. ' +
          'Empty data received')
      } else if (data.length === 0) {
        /* Valid data, but no watchers. */
        dbg('Got empty watchers list')
        return
      }

      commit('ADD_WATCHERS', data)

      const savedPagination = VoicemailLocalStorage.getPagination()
      const savedFilters = VoicemailLocalStorage.getFilters()
      if (state.watchers) {
        const savedActiveWatchers = VoicemailLocalStorage.getActiveWatchers()
        if (!savedActiveWatchers || savedActiveWatchers.length === 0) {
          state.watchers.forEach((watcher, index) => {
            if (rootGetters['sipua/exten'] === watcher?.username) {
              commit('SET_ACTIVE_WATCHERS_RAW', [watcher])
            }
          })
        } else {
          commit('SET_ACTIVE_WATCHERS_RAW', savedActiveWatchers)
        }
      }
      if (savedPagination) {
        savedPagination.curPage = 1
        savedPagination.start = 0
        commit('SET_PAGINATION', savedPagination)
      }
      if (savedFilters) {
        commit('SET_FILTER', savedFilters)
      }

      const users = (new Set(state.activeWatchers.map(w => w.username)).size > 0) ? [...new Set(state.activeWatchers.map(w => w.username))] : [rootGetters.sipuaExten]
      const start = state.pagination.start
      const limit = state.pagination.perPage
      const sortCol = state.sortCol
      const sortDir = state.sortDir
      const search = rootGetters['search/searchText']
      const filter = state.filter

      return Promise.all([
        API.getVoicemails({ users, start, limit, sortCol, sortDir, search, filter }),
        // null user for current user
        API.getVMByFolder(users, 'archive')
      ]).then(([vms, archives]) => {
        commit('INIT_PAGINATION', vms.data.total)
        dispatch('initVoicemails', vms.data)
        dispatch('loadBoxSettings')
        dispatch('getPinRestriction')
        dispatch('getCustomFolders')
        dispatch('getCategories')
        commit('ADD_ARCHIVES', archives.data.data)
        commit('SET_INITIAL_LOADING', false)
      })
    })
  },

  getVoicemailById: async ({ commit }, voicemailId) => {
    events.mask()
    try {
      const response = await API.getVoicemailById(voicemailId)
      commit('SET_MESSAGE_DETAIL', response.data)
      events.unmask()
    } catch (error) {
      dbg('Error loading voicemail by ID:', error)
      events.unmask()
    }
  },

  loadVoicemailsForUsers: async ({ dispatch, commit }) => {
    events.mask()
    commit('SET_PAGE', 1) // Ensure pagination starts from page 1
    try {
      await Promise.allSettled([
        dispatch('loadPage'),
        dispatch('loadBoxSettings'),
        dispatch('getCustomFolders'),
        dispatch('getCategories'),
        dispatch('loadArchives')
      ])
    } catch (err) {
      dbg('Failed to load voicemails')
      dbg(err.response)
      events.pubError(API.getException(err) || 'Failed to load voicemails')
    }
    events.unmask()
  },

  getCustomFolders: function ({ commit }) {
    return API.getVoicemailCustomFolders().then(({ data }) => {
      commit('SET_CUSTOM_FOLDERS', data)
    })
  },

  getCategories: function ({ commit }) {
    return API.getVoicemailCategories().then(({ data }) => {
      commit('SET_CATEGORIES', data)
    })
  },

  getPinRestriction: function ({ commit }) {
    dbg('Getting vmb pin restrictions')
    return API.getVmbPinRestriction().then(({ data }) => {
      commit('SET_PIN_RESTRICTIONS', data)
    })
  },

  smartFavoriteSwitch: function ({ dispatch }, voicemail) {
    if (voicemail.favorite) {
      dispatch('unmarkVoicemailFavorite', voicemail.id)
    } else {
      dispatch('markVoicemailFavorite', voicemail.id)
    }
  },

  markVoicemailFavorite: function ({ commit }, voicemailId) {
    try {
      API.markVmFavorite(voicemailId).then(({ data }) => {
        commit('SET_FAVORITE', data)
      })
    } catch (err) {
      dbg('Failed to mark voicemail as favorite')
      events.pubError(API.getException(err) || 'Failed to mark voicemail as favorite')
    }
  },

  unmarkVoicemailFavorite: function ({ commit }, voicemailId) {
    try {
      API.unmarkVmFavorite(voicemailId).then(({ data }) => {
        commit('SET_FAVORITE', data)
      })
    } catch (err) {
      dbg('Failed to unmark voicemail as favorite')
      events.pubError(API.getException(err) || 'Failed to unmark voicemail as favorite')
    }
  },

  bulkFavorite: function ({ commit }, voicemailIds) {
    try {
      API.bulkFavorite(voicemailIds).then(({ data }) => {
        data.forEach(msg => {
          commit('SET_FAVORITE', msg)
        })
      })
    } catch (err) {
      dbg('Failed to favorite voicemails')
      events.pubError(API.getException(err) || 'Failed to favorite voicemails')
    }
  },

  bulkUnfavorite: function ({ commit }, voicemailIds) {
    try {
      API.bulkUnfavorite(voicemailIds).then(({ data }) => {
        data.forEach(msg => {
          commit('SET_FAVORITE', msg)
        })
      })
    } catch (err) {
      dbg('Failed to unfavorite voicemails')
      events.pubError(API.getException(err) || 'Failed to unfavorite voicemails')
    }
  },

  loadArchives: function ({ commit, rootGetters }) {
    const users = (new Set(state.activeWatchers.map(w => w.username)).size > 0) ? [...new Set(state.activeWatchers.map(w => w.username))] : [rootGetters.sipuaExten]
    dbg(`Load Archives for ${users}`)
    return API.getVMByFolder(users, 'archive')
      .then(({ data: { data } }) => {
        commit('ADD_ARCHIVES', data)
      })
  },

  setLimitPerPage: function ({ commit }, perPage) {
    commit('SET_LIMIT_PER_PAGE', perPage)
  },

  initVoicemails: function ({ commit }, data) {
    if (!data.success) {
      dbg('Invalid voicemails data: %o', data)
      return
    }
    commit('RESET_MESSAGES')
    dbg(`Init voicemails with ${data.total} messages`)
    const unreadCount = data?.unread_count
    const vmsgs = data.data ? data.data : []
    vmsgs.forEach(msg => {
      commit('ADD_MESSAGE', msg)
    })
    commit('SET_UNREAD_COUNT', unreadCount)
    commit('sipua/SET_VMNUM', unreadCount, { root: true })
  },

  mwiUpdate: function ({ dispatch, rootGetters }, data) {
    const mbox = rootGetters.uaMailbox
    if (mbox !== data.mailbox) {
      dbg(`Local user ${mbox} doesn't match MWI mailbox ${data.mailbox}. Skip update vm`)
      return
    }
    dbg(`Update voicemail for the box ${mbox} new messages: ${data.new}`)
    dispatch('reloadPage')
  },

  loadBoxSettings: function ({ commit, state }) {
    const boxID = state.activeWatchers?.[0]?.voicemail_box_id
    API.getVMBSettings(boxID).then(({ data }) => {
      commit('INIT_SETTINGS', data)
    })
  },

  loadPage: async ({ commit, dispatch }) => {
    commit('RESET_PAGINATION')
    await dispatch('reloadPage')
  },

  reloadPage: async ({ commit, state, dispatch, rootGetters }) => {
    const users = (new Set(state.activeWatchers.map(w => w.username)).size > 0) ? [...new Set(state.activeWatchers.map(w => w.username))] : [rootGetters.sipuaExten]
    const start = state.pagination.start
    const limit = state.pagination.perPage
    const filter = state.filter
    const sortCol = state.sortCol
    const sortDir = state.sortDir
    const search = rootGetters['search/searchText']
    dbg(`Load voicemail messages for ${users ?? 'callpanel user'}`)
    commit('RESET_MESSAGES')

    const resp = await API.getVoicemails({ users, start, limit, sortCol, sortDir, search, filter })
    commit('INIT_PAGINATION', resp.data.total)
    dispatch('initVoicemails', resp.data)
  },

  setActiveWatchers: function ({ commit }, owners) {
    commit('SET_ACTIVE_WATCHERS', owners)
    commit('SET_PAGE', 1)
  },

  setFilter: function ({ commit }, filter) {
    commit('SET_FILTER', filter)
    VoicemailLocalStorage.setFilters(filter)
  },

  setGreetData: function ({ commit }, { data, folder }) {
    commit('ADD_GREETING', { data, folder })
  },

  setPage: function ({ commit }, page) {
    commit('SET_PAGE', page)
  },

  setSortCol: function ({ commit }, col) {
    commit('SET_SORT_COL', col)
  },

  setSortDir: function ({ commit }, dir) {
    commit('SET_SORT_DIR', dir)
  },

  setVBEmail: function ({ commit }, email) {
    commit('SET_VB_EMAIL', email)
  },

  setVBPager: function ({ commit }, email) {
    commit('SET_VB_PAGER', email)
  },

  setVBPin: function ({ commit }, pin) {
    commit('SET_VB_PIN', pin)
  },

  setVBAttach: function ({ commit }, enabled) {
    commit('SET_VB_ATTACH', enabled)
  },

  setVBDeleteVM: function ({ commit }, enabled) {
    commit('SET_VB_DELETE', enabled)
  },

  setVBPlayCID: function ({ commit }, enabled) {
    commit('SET_VB_PLAYCID', enabled)
  },

  setVBEnvelope: function ({ commit }, enabled) {
    commit('SET_VB_ENVELOPE', enabled)
  },

  setVBTranscript: function ({ commit }, enabled) {
    commit('SET_VB_TRANSCRIPT', enabled)
  },

  setVBSummarize: function ({ commit }, enabled) {
    commit('SET_VB_SUMMARIZE', enabled)
  },

  setVBCategorize: function ({ commit }, enabled) {
    commit('SET_VB_CATEGORIZE', enabled)
  },

  setVBTranslate: function ({ commit }, enabled) {
    commit('SET_VB_TRANSLATE', enabled)
  },

  setVBTranslationLanguage: function ({ commit }, translationLanguage) {
    commit('SET_VB_TRANSLATION_LANGUAGE', translationLanguage)
  },

  setVBSkipInstruct: function ({ commit }, enabled) {
    commit('SET_VB_SKIP_INSTRUCT', enabled)
  },

  toggleRead: function ({ commit, getters }, id) {
    commit('TOGGLE_READ', id)
    commit('sipua/SET_VMNUM', getters.totalNewMsg, { root: true })
  },

  setAsRead: function ({ commit, getters }, id) {
    commit('SET_AS_READ', id)
    commit('sipua/SET_VMNUM', getters.totalNewMsg, { root: true })
  },

  transferMsg: async ({ dispatch }, { id, dest }) => {
    dbg(`Transfer voicemail to ${dest}`)
    try {
      await API.VMXferTo(id, dest)
      events.pubNotify({
        status: 'success',
        text: 'vmail.notify.xferOk',
        args: { callerid: id, to: dest }
      })
    } catch (err) {
      dbg(err)
      events.pubError(API.getException(err) || 'Failed to transfer voicemail with API')
    }
    await dispatch('reloadPage')
  },

  deleteMsg: async ({ commit, getters, dispatch }, id) => {
    dbg('Delete voicemail')
    try {
      await API.removeVMailMsg(id)
      commit('DELETE_MSG', id)
    } catch (err) {
      dbg(err)
      events.pubError(API.getException(err) || 'Failed to delete voicemail with API')
    }
    await dispatch('reloadPage')
  },

  moveToCustomFolder: async ({ dispatch }, { id, customFolder }) => {
    dbg('Move voicemail to custom folder')
    try {
      await API.moveToCustomFolder(id, customFolder)
    } catch (err) {
      dbg(err)
      events.pubError(API.getException(err) || 'Failed to move voicemail into custom folder with API')
    }
    await dispatch('getCustomFolders')
    await dispatch('reloadPage')
  },

  bulkMoveToCustomFolder: async ({ dispatch }, { ids, customFolder }) => {
    dbg('Bulk move voicemail to custom folder')
    try {
      await API.bulkMoveToCustomFolder(ids, customFolder)
    } catch (err) {
      dbg(err)
      events.pubError(API.getException(err) || 'Failed to bulk move voicemail into custom folder with API')
    }
    await dispatch('getCustomFolders')
    await dispatch('reloadPage')
  },

  removeFromCustomFolder: async ({ dispatch }, { id }) => {
    dbg('Move voicemail out of custom folder to old')
    try {
      await API.removeFromCustomFolder(id)
    } catch (err) {
      dbg(err)
      events.pubError(API.getException(err) || 'Failed to move voicemail out of custom folder with API')
    }
    await dispatch('getCustomFolders')
    await dispatch('reloadPage')
  },

  bulkRemoveFromCustomFolder: async ({ dispatch }, { ids }) => {
    dbg('Bulk remove voicemail from custom folder')
    try {
      await API.bulkRemoveFromCustomFolder(ids)
    } catch (err) {
      dbg(err)
      events.pubError(API.getException(err) || 'Failed to bulk remove voicemails from custom folder with API')
    }
    await dispatch('getCustomFolders')
    await dispatch('reloadPage')
  },

  bulkMarkAsRead: async ({ dispatch }, ids) => {
    dbg('Mark voicemails as read')
    try {
      await API.VMBulkMarkRead(ids)
      events.pubNotify({
        status: 'success',
        text: 'vmail.notify.bulkMarkReadOk',
        args: { count: ids.length }
      })
    } catch (err) {
      dbg(err)
      events.pubError(API.getException(err) || 'Failed to mark voicemails as read with API')
    }
    await dispatch('reloadPage')
  },

  bulkMarkAsUnread: async ({ dispatch }, ids) => {
    dbg('Mark voicemails as unread')
    try {
      await API.VMBulkMarkNew(ids)
      events.pubNotify({
        status: 'success',
        text: 'vmail.notify.bulkMarkUnreadOk',
        args: { count: ids.length }
      })
    } catch (err) {
      dbg(err)
      events.pubError(API.getException(err) || 'Failed to mark voicemails as unread with API')
    }
    await dispatch('reloadPage')
  },

  bulkTransferMsg: async ({ dispatch }, { ids, dest }) => {
    dbg(`Transfer voicemails to ${dest}`)
    try {
      await API.VMBulkTransfer(ids, [dest])
      events.pubNotify({
        status: 'success',
        text: 'vmail.notify.bulkXferOk',
        args: { count: ids.length, to: dest }
      })
    } catch (err) {
      dbg(err)
      events.pubError(API.getException(err) || 'Failed to transfer voicemails with API')
    }
    await dispatch('reloadPage')
  },

  bulkDeleteMsg: async ({ dispatch }, ids) => {
    dbg('Delete voicemails')
    try {
      await API.VMBulkDelete(ids)
      events.pubNotify({
        status: 'success',
        text: 'vmail.notify.bulkDelOk',
        args: { count: ids.length }
      })
    } catch (err) {
      dbg(err)
      events.pubError(API.getException(err) || 'Failed to delete voicemails with API')
    }
    await dispatch('reloadPage')
  },

  setDrawerState: ({ commit }, drawerState) => {
    commit('SET_DRAWER_STATE', drawerState)
  },

  saveSettings: async ({ commit, dispatch }, vmbox) => {
    const bool2pbx = (val) => (val ? 'yes' : 'no')
    const settings = {
      password: state.settings.pin,
      email: state.settings.email,
      pagerdest: state.settings.pager,
      translation_language: state.settings.translationLanguage,
      attach: bool2pbx(state.settings.attach),
      custom_deletevoicemail: bool2pbx(state.settings.deletevm),
      saycid: bool2pbx(state.settings.playcid),
      envelope: bool2pbx(state.settings.envelope),
      transcription: bool2pbx(state.settings.transcript),
      summarize: bool2pbx(state.settings.summarize),
      categorize: bool2pbx(state.settings.categorize),
      translation: bool2pbx(state.settings.translate),
      skip_instruction_on_new_vm: bool2pbx(state.settings.skipInstruct)
    }

    API.saveVMSettings(state.settings.id, settings)
      .then(() => {
        events.pubNotify({
          text: 'vmail.notify.savedOk',
          status: 'success'
        })
      })
      .catch((err) => {
        this.dbg(err)
        const apiException = err.response?.data?.exception
        let errorMessage = this.$t('vmail.notify.saveFail')
        if (apiException) {
          errorMessage += `: ${apiException}`
        }
        events.pubError(errorMessage)
      })
  }
}

const mutations = {
  SET_DRAWER_STATE: function (state, drawerState) {
    state.drawerState = drawerState
  },

  SET_CATEGORIES: function (state, categories) {
    state.categories = categories
  },
  // for skeleton loading
  SET_INITIAL_LOADING (state, initialLoading) {
    state.initialLoading = initialLoading
  },

  SET_UNREAD_COUNT: function (state, unreadCount) {
    state.totalNewMsg = unreadCount || 0
  },

  RESET_MESSAGES (state) {
    state.messages.splice(0)
    state.greet.greeting = {}
    state.greet.busy = {}
    state.greet.unavailable = {}
    state.greet.temp = {}
  },

  RESET_PAGINATION (state) {
    state.pagination.total = 0
    state.pagination.start = 0
    VoicemailLocalStorage.setPagination(state.pagination)
  },

  INIT_PAGINATION (state, total) {
    if (state.pagination.curPage <= 1) {
      state.pagination.total = total
    }
    state.pagination.pages = Math.ceil(total / state.pagination.perPage)
    state.pagination.limit = state.pagination.perPage
    // VoicemailLocalStorage.setPagination(state.pagination)
  },

  SET_PAGINATION (state, pagination) {
    state.pagination = pagination
  },

  UPDATE_PAGINATION (state, total) {
    state.pagination.pages = Math.ceil(total / state.pagination.perPage)
    state.pagination.limit = state.pagination.perPage
    VoicemailLocalStorage.setPagination(state.pagination)
  },

  SET_PAGE (state, page) {
    state.pagination.curPage = page > 0 ? page : 1
    state.pagination.start = (state.pagination.curPage - 1) * state.pagination.perPage
    VoicemailLocalStorage.setPagination(state.pagination)
  },

  ADD_WATCHERS (state, data) {
    if (!data) {
      dbg('voicemail watchers data is empty. Skip setup vm watchers')
      return
    }
    const watchers = []
    data.forEach(w => {
      watchers.push(w)
    })
    state.watchers = watchers
    state.activeWatchers = [data?.[0] || null]
  },

  RESET_WATCHERS (state) {
    state.watchers.splice(0)
  },

  SET_FAVORITE (state, data) {
    const vmIndex = state.messages.findIndex(vm => vm.id === data.id)
    Vue.set(state.messages, vmIndex, new VMail(data))
  },

  SET_ACTIVE_WATCHERS (state, owners) {
    const w = []
    owners.forEach(owner => {
      state.watchers.forEach((watcher, index) => {
        if (owner === watcher?.username) {
          w.push(watcher)
        }
      })
    })
    state.activeWatchers = w
    VoicemailLocalStorage.setActiveWatchers(w)
  },

  SET_ACTIVE_WATCHERS_RAW (state, watchers) {
    state.activeWatchers = watchers
  },

  SET_CUSTOM_FOLDERS (state, folders) {
    state.customFolders = folders
  },

  SET_PIN_RESTRICTIONS (state, data) {
    state.pinRestrictions = data
  },

  INIT_SETTINGS (state, data) {
    if (!data) {
      dbg('Empty data for voicemail settings')
      return
    }
    state.settings.id = data.uniqueid
    state.settings.mailbox = data.mailbox
    state.settings.language = data.language
    state.settings.pin = data.password
    state.settings.email = data.email
    state.settings.pager = data.pagerdest ? data.pagerdest : ''
    state.settings.translationLanguage = data.translation_language

    // pbx bool
    state.settings.attach = pbx2bool(data.attach)
    state.settings.deletevm = pbx2bool(data.custom_deletevoicemail)
    state.settings.playcid = pbx2bool(data.saycid)
    state.settings.envelope = pbx2bool(data.envelope)
    state.settings.transcript = pbx2bool(data.transcription)
    state.settings.summarize = pbx2bool(data.summarize)
    state.settings.categorize = pbx2bool(data.categorize)
    state.settings.translate = pbx2bool(data.translation)
    state.settings.skipInstruct = pbx2bool(data.skip_instruction_on_new_vm)
  },

  RESET_SETTINGS (state) {
    state.settings.id = null
    state.settings.mailbox = ''
    state.language = 'en'
    state.settings.pin = ''
    state.settings.email = ''
    state.settings.pager = ''
    state.settings.attach = false
    state.settings.deletevm = false
    state.settings.playcid = false
    state.settings.envelope = false
    state.settings.transcript = false
    state.settings.summarize = false
    state.settings.categorize = false
    state.settings.translate = false
    state.settings.translationLanguage = null
    state.settings.skipInstruct = false
  },

  SET_VB_EMAIL (state, email) {
    state.settings.email = email
  },

  SET_VB_PAGER (state, email) {
    state.settings.pager = email
  },

  SET_VB_PIN (state, pin) {
    state.settings.pin = pin
  },

  SET_VB_ATTACH (state, enabled) {
    state.settings.attach = enabled
  },

  SET_VB_DELETE (state, enabled) {
    state.settings.deletevm = enabled
  },

  SET_VB_PLAYCID (state, enabled) {
    state.settings.playcid = enabled
  },

  SET_VB_ENVELOPE (state, enabled) {
    state.settings.envelope = enabled
  },

  SET_VB_TRANSCRIPT (state, enabled) {
    state.settings.transcript = enabled
  },

  SET_VB_SUMMARIZE (state, enabled) {
    state.settings.summarize = enabled
  },

  SET_VB_CATEGORIZE (state, enabled) {
    state.settings.categorize = enabled
  },

  SET_VB_TRANSLATE (state, enabled) {
    state.settings.translate = enabled
  },

  SET_VB_TRANSLATION_LANGUAGE (state, lang) {
    state.settings.translationLanguage = lang
  },

  SET_VB_SKIP_INSTRUCT (state, enabled) {
    state.settings.skipInstruct = enabled
  },

  SET_FILTER (state, filter) {
    state.filter = filter
  },

  SET_SORT_COL (state, col) {
    state.sortCol = col
  },

  SET_SORT_DIR (state, dir) {
    state.sortDir = dir
  },

  ADD_MESSAGE (state, data) {
    const msg = new VMail(data)
    dbg(`Add message from ${data.callerid}`)
    state.messages.push(msg)
  },

  SET_MESSAGE_DETAIL (state, data) {
    const msg = new VMail(data)
    dbg(`Set message detail for ${data.callerid}`)
    state.messageDetail = msg
  },

  ADD_ARCHIVES (state, data) {
    state.archives = []
    data.forEach(msg => {
      state.archives.push(msg)
    })
  },

  ADD_GREETING (state, { data, folder }) {
    const msg = greetMsg(data?.data)
    if (!msg) {
      dbg(`Greeting data for forlder ${folder} is empty`)
    }
    switch (folder) {
      case 'greet':
        state.greet.greeting = msg
        break
      case 'busy':
        state.greet.busy = msg
        break
      case 'unavail':
        state.greet.unavailable = msg
        break
      case 'temp':
        state.greet.temp = msg
        break
    }
  },

  TOGGLE_READ (state, id) {
    const vm = state.messages.find(m => m.id === id)
    if (!vm) {
      dbg(`ERROR: Failed TOGGLE_READ. Voicemail with ID '${id}' not found.`)
      return
    }

    if (!vm.isRead && state.totalNewMsg > 0) {
      state.totalNewMsg--
    } else {
      state.totalNewMsg++
    }

    vm.isRead = !vm.isRead
    vm.folder = vm.isRead ? 'old' : 'inbox'
    API.toggleVMailRead(id, vm.isRead).catch(err => {
      events.pubError(API.getException(err) ||
        'Failed to toggle voicemail read status with API.')
      dbg(err)
      if (!vm.isRead) {
        state.totalNewMsg++
      } else {
        state.totalNewMsg--
      }
    })
  },

  SET_AS_READ (state, id) {
    const vm = state.messages.find(m => m.id === id)
    if (vm && !vm.isRead) {
      vm.isRead = true
      vm.folder = 'old'
      if (state.totalNewMsg > 0) {
        state.totalNewMsg--
      }
    }
  },

  DELETE_MSG (state, id) {
    const idx = state.messages.findIndex(m => m.id === id)
    if (idx === -1) {
      dbg(`ERROR: Failed DELETE_MSG. Voicemail with ID '${id}' not found.`)
      return
    }
    state.pagination.total--
    state.messages.splice(idx, 1)
    VoicemailLocalStorage.setPagination(state.pagination)
  },

  SET_LIMIT_PER_PAGE (state, perPage) {
    state.pagination.perPage = perPage
    VoicemailLocalStorage.setPagination(state.pagination)
  }
}

const state = {
  drawerState: true,
  totalNewMsg: 0,
  messages: [],
  messageDetail: null,
  archives: [],
  watchers: [],
  activeWatchers: [],
  pinRestrictions: {},
  customFolders: [],
  categories: [],
  initialLoading: true,
  settings: {
    id: null,
    mailbox: '',
    pin: '',
    email: '',
    pager: '',
    language: 'en',
    attach: null,
    deletevm: null,
    playcid: null,
    envelope: null,
    transcript: null,
    summarize: null,
    categorize: null,
    translate: null,
    translationLanguage: null,
    skipInstruct: null
  },
  greet: {
    greeting: {},
    busy: {},
    unavailable: {},
    temp: {}
  },
  pagination: {
    start: 0,
    perPage: 18,
    pages: 0,
    curPage: 1,
    total: 0
  },
  sortCol: 'origtime',
  sortDir: 'DESC',
  filter: {
    folder: 'all',
    category: 'all',
    date: ''
  }
}

const getters = {
  drawerState: state => state.drawerState,
  categories: state => state.categories,
  messageDetail: state => state.messageDetail,
  initialLoading: state => state.initialLoading,
  activeWatchers: state => state.activeWatchers,
  pinRestrictions: state => state.pinRestrictions,
  archives: state => state.archives,
  greetMsgID: state => name => state.greet[name].id,
  hasWarchers: state => state.watchers.length > 0,
  messages: state => state.messages,
  pagination: state => state.pagination,
  showPagination: state => state.total > state.pagination.perPage,
  sortCol: state => state.sortCol,
  sortDir: state => state.sortDir,
  totalMsgs: state => state.total,
  filter: state => state.filter,
  totalNewMsg: state => state.totalNewMsg,
  vmboxSettings: state => state.settings,
  watchers: state => state.watchers,
  watcherBoxID: state => state.activeWatchers?.[0]?.voicemail_box_id || '',
  watcherUserID: state => state.activeWatchers?.[0]?.user_id || '',
  watcherSubscriberID: state => state.activeWatchers?.[0]?.subscriber_id || '',
  watcherUserName: state => state.activeWatchers?.[0]?.username || '',
  customFolders: state => state.customFolders
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
